import React, { useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function mapAuthError(err) {
  switch (err.code) {
    case 'auth/user-not-found':
      return 'Nie znaleziono użytkownika o podanym adresie email.';
    case 'auth/wrong-password':
      return 'Nieprawidłowe hasło.';
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'Nieprawidłowy email lub hasło.';
    case 'auth/user-disabled':
      return 'Konto wyłączone — skontaktuj się z administratorem (wymagana aktywacja).';
    case 'auth/email-already-in-use':
      return 'Ten adres email jest już zarejestrowany.';
    case 'auth/invalid-email':
      return 'Nieprawidłowy adres email.';
    case 'auth/popup-closed-by-user':
      return 'Logowanie zostało anulowane.';
    case 'auth/account-exists-with-different-credential':
      return 'Konto z tym adresem email już istnieje z inną metodą logowania.';
    case 'auth/domain-not-allowed':
      return err.message;
    default:
      return err.message;
  }
}

export default function LoginPage() {
  const { login, register, loginMicrosoft, loginGoogle, settings } = useAuthContext();
  const navigate = useNavigate();

  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isRegister && password !== confirmPassword) {
      setError('Hasła nie są identyczne!');
      return;
    }

    if (password.length < 6) {
      setError('Hasło musi mieć co najmniej 6 znaków.');
      return;
    }

    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password);
      } else {
        await login(email, password);
      }
      navigate('/browse');
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginMicrosoft();
      navigate('/browse');
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginGoogle();
      navigate('/browse');
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-10">
        {/* Logo */}
        <div className="flex items-center justify-center mb-4">
          <img src="/logo_giode.png" alt="G-ODE" className="h-8" />
        </div>

        {/* Tytuł */}
        <h1 className="text-3xl font-bold text-center mb-1 text-gray-900">
          Generator przekrojów
        </h1>
        <p className="text-center text-gray-400 mb-8 text-sm">
          System generowania przekrojów geologicznych
        </p>

        {/* Błędy */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Formularz */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Adres email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-brand-700 transition text-gray-800"
              placeholder="twoj@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Hasło
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-brand-700 transition text-gray-800"
              placeholder="••••••••••••••"
            />
          </div>

          {isRegister && (
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                Potwierdź hasło
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-brand-700 transition text-gray-800"
                placeholder="Wpisz hasło ponownie"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-700 hover:bg-brand-800 text-white py-3 px-4 rounded-lg font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading
              ? 'Proszę czekać...'
              : isRegister
              ? 'Zarejestruj się'
              : 'Zaloguj się'}
          </button>
        </form>

        {/* Separator */}
        {(settings.googleEnabled || settings.microsoftEnabled) && (
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-4 text-sm text-gray-400">lub</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>
        )}

        {/* Przycisk Google */}
        {settings.googleEnabled && (
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 border-2 border-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed transition mb-3"
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571c.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            Zaloguj przez Google
          </button>
        )}

        {/* Przycisk Microsoft */}
        {settings.microsoftEnabled && (
          <button
            onClick={handleMicrosoftLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-gray-900 hover:bg-gray-800 text-white py-3 px-4 rounded-lg font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <svg width="20" height="20" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
            Zaloguj przez Microsoft
          </button>
        )}

        {/* Link rejestracja/logowanie */}
        <div className="mt-5 text-center">
          <button
            onClick={() => {
              setIsRegister(!isRegister);
              setError('');
            }}
            className="text-sm hover:underline text-brand-700"
          >
            {isRegister
              ? 'Masz już konto? Zaloguj się'
              : 'Nie masz konta? Zarejestruj się'}
          </button>
        </div>

        {/* Informacja */}
        <p className="text-center text-gray-400 text-xs mt-6">
          Dostęp tylko dla autoryzowanych użytkowników
        </p>
      </div>
    </div>
  );
}
